import { ConfigProvider, Table, Tooltip } from 'antd';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CookieService from '../services/CookieService';
import axiosInstance from '../utils/axiosConfig';
import { useAppConfig } from './AppConfigProvider';
import FilterRow from './FilterRow';
// import { Table } from './LazyAntdComponents';
import PdfChip from './PdfChip';
import StatusChip from './StatusChip';

// Helper function to determine status from API data
const mapApiToStatus = (item) => {
  // This is a sample logic - adjust according to your actual business rules
  if (item.einvoiceId || item.hasPdf) {
    return 'SUBMITTED'; // If it has e-invoice ID or PDF, it's submitted
  } else if (item.InvcHead_InvoiceComment &&
    (item.InvcHead_InvoiceComment.includes('error') ||
      item.InvcHead_InvoiceComment.toLowerCase().includes('fault'))) {
    return 'ERROR';     // If comment contains error-related text
  } else if (item.InvcHead_InvoiceComment &&
    item.InvcHead_InvoiceComment.toLowerCase().includes('red')) {
    return 'RED_NOTE';  // If comment contains 'red', it's a red note
  } else {
    return 'PENDING';   // Default status
  }
};

// 统一格式化发票过账日期
const formatPostDate = (raw) => {
  if (raw === null || raw === undefined || raw === '') return '--';

  if (typeof raw === 'string') {
    const str = raw.trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [year, month, day] = str.split('-');
      // 手动拼接为 MM/DD/YYYY，避免任何时区运算
      return `${month}/${day}/${year}`;
    }
  }

  return '--';
};

// Function to transform API response data
const transformInvoiceData = (apiData) => {
  return apiData.map(item => {
    // Calculate total amount from invoice details if available
    let calculatedAmount = item.invoiceAmount !== undefined && item.invoiceAmount !== null ?
      parseFloat(item.invoiceAmount) : 0;

    if (item.invoiceDetails && item.invoiceDetails.length > 0) {
      const subtotal = item.invoiceDetails.reduce((sum, detail) =>
        sum + (parseFloat(detail.dspExtPrice || detail.docExtPrice || 0)), 0);

      const taxTotal = item.invoiceDetails.reduce((sum, detail) => {
        // Use backend calculated tax amount if available, otherwise calculate
        const taxAmount = detail.dspLineTax !== undefined && detail.dspLineTax !== null
          ? parseFloat(detail.dspLineTax || 0)
          : parseFloat(detail.docExtPrice || 0) * parseFloat(detail.taxPercent || 0) / 100;
        return sum + taxAmount;
      }, 0);

      calculatedAmount = subtotal + taxTotal;
    }

    return {
      id: item.erpInvoiceId.toString(),
      postDate: formatPostDate(item.orderDate),
      type: item.fapiaoType || '--',
      customerName: item.customerName || '--',
      amount: calculatedAmount,
      calculatedFromDetails: !!item.invoiceDetails && item.invoiceDetails.length > 0,
      invoiceComment: item.invoiceComment || '',
      status: item.status,
      hasPdf: !!item.eInvoicePdf || item.hasPdf,
      orderNum: item.orderNumber,
      poNum: item.poNumber,
      einvoiceId: item.eInvoiceId,
      einvoiceDate: item.eInvoiceDate ? new Date(item.eInvoiceDate).toLocaleDateString() : '--',
      submittedBy: item.submittedBy,
      invoiceDetails: item.invoiceDetails || [],
      eInvoicePdf: item.eInvoicePdf,
      comment: item.comment || '',
      invoiceAmount: item.invoiceAmount,
    };
  });
};

// 租户 ERP URL 配置映射
const TENANT_URL_CONFIG = {
  'rtszh': {
    baseUrl: 'https://rts-connect-pilot.epicorsaas.com/server/apps/erp/home',
    path: '#/view/OMGO3010',
    channelId: '4d19bdda-8fb0-4703-8d2b-474deeb410a7',
    company: 'RTSZH',
    site: 'MfgSys'
  },
  'SIMALFA': {
    baseUrl: 'https://simalfa.kineticcloud.cn/SIMALFAProd/Apps/Erp/Home',
    path: '#/view/OMGO3010/Erp.UI.ARInvoiceTracker',
    channelId: '12389eab-5d7a-4288-8621-30a9741c3a24',
    company: null, // 使用配置中的 companyID
    site: 'MfgSys'
  },
  'SIMALFATEST': {    
    baseUrl: 'https://simalfa.kineticcloud.cn/SIMALFATest/Apps/Erp/Home',    
    path: '#/view/OMGO3010/Erp.UI.ARInvoiceTracker',    
    channelId: '12389eab-5d7a-4288-8621-30a9741c3a24',    
    company: null, // 或者固定测试环境的 company，如 'SIMALFAT'    
    site: 'MfgSys',  
    },
  'XAAR': {    
    baseUrl: 'https://seasiadtedu00.epicorsaas.com/SaaS951/Apps/ERP/Home',    
    path: '#/view/OMGO3010/Erp.UI.ARInvoiceTracker',    
    channelId: '15db1bad-48aa-4cc5-a33b-b922639617c5',    
    company: '169726E6', // 或者固定测试环境的 company，如 'SIMALFAT'    
    site: 'MfgSys',  
    },
};

// 默认配置（当租户不在映射中时使用）
const DEFAULT_TENANT_CONFIG = TENANT_URL_CONFIG['SIMALFA'];

// Function to generate ERP URL based on tenant
const generateErpUrl = (invoiceNum, config) => {
  try {
    // 获取用户信息中的 tenantName
    const userInfo = CookieService.getUserInfo();
    const tenantName = userInfo?.tenantName;

    console.log('生成ERP链接 - tenantName:', tenantName, 'invoiceNum:', invoiceNum);

    // 大小写模糊匹配
    const serverBaseUrlRaw =
      config?.settings?.serverSettings?.serverBaseAPI ||
      '';

    const serverBaseUrl = typeof serverBaseUrlRaw === 'string'
      ? serverBaseUrlRaw.toLowerCase()
      : '';

    // 根据 tenantName 和后端 Base URL 选择正确的租户配置
    let tenantConfig = TENANT_URL_CONFIG[tenantName] || DEFAULT_TENANT_CONFIG;

    if (
      tenantName === 'SIMALFA' &&
      typeof serverBaseUrl === 'string' &&
      serverBaseUrl.includes('simalfatest') &&
      TENANT_URL_CONFIG.SIMALFATEST
    ) {
      tenantConfig = TENANT_URL_CONFIG.SIMALFATEST;
    }

    // 确定公司参数
    const company = tenantConfig.company || config?.settings?.serverSettings?.companyID || "";

    // 构建 URL
    const url = `${tenantConfig.baseUrl}/${tenantConfig.path}?channelid=${tenantConfig.channelId}&layerVersion=0&baseAppVersion=0&company=${company}&site=${tenantConfig.site}&pageId=Details&KeyFields.InvoiceNum=${invoiceNum}&pageChanged=true`;

    console.log('生成的ERP链接:', url);
    return url;

  } catch (error) {
    console.error('生成ERP链接时出错:', error);
    // 出错时使用默认配置
    const company = config?.settings?.serverSettings?.companyID || "";
    return `${DEFAULT_TENANT_CONFIG.baseUrl}/${DEFAULT_TENANT_CONFIG.path}?channelid=${DEFAULT_TENANT_CONFIG.channelId}&layerVersion=0&baseAppVersion=0&company=${company}&site=${DEFAULT_TENANT_CONFIG.site}&pageId=Details&KeyFields.InvoiceNum=${invoiceNum}&pageChanged=true`;
  }
};

// Function to build query parameters for API request
const buildApiQueryParams = (filterValues) => {
  const params = new URLSearchParams();

  if (filterValues.page) params.append('page', filterValues.page);
  if (filterValues.limit) params.append('limit', filterValues.limit);

  // 映射前端过滤字段到API参数
  if (filterValues.id) params.append('erpInvoiceId', filterValues.id);
  if (filterValues.customerName) params.append('customerName', filterValues.customerName);
  if (filterValues.status) params.append('status', filterValues.status);
  if (filterValues.einvoiceId) params.append('eInvoiceId', filterValues.einvoiceId);
  // Invoice Amount
  if (
    filterValues.amount !== undefined &&
    filterValues.amount !== null &&
    String(filterValues.amount).trim() !== ''
  ) {
    params.append('invoiceAmount', String(filterValues.amount).trim());
  }

  // Handle date ranges
  if (filterValues.postDate) {
    if (typeof filterValues.postDate === 'object' && filterValues.postDate.start && filterValues.postDate.end) {
      // It's a date range
      params.append('startDate', filterValues.postDate.start);
      params.append('endDate', filterValues.postDate.end);
    } else if (typeof filterValues.postDate === 'string') {
      // For backward compatibility with single date
      params.append('startDate', filterValues.postDate);
    }
  }

  if (filterValues.einvoiceDate) {
    if (typeof filterValues.einvoiceDate === 'object' && filterValues.einvoiceDate.start && filterValues.einvoiceDate.end) {
      // It's a date range
      params.append('eInvoiceStartDate', filterValues.einvoiceDate.start);
      params.append('eInvoiceEndDate', filterValues.einvoiceDate.end);
    } else if (typeof filterValues.einvoiceDate === 'string') {
      // For backward compatibility with single date
      params.append('eInvoiceDate', filterValues.einvoiceDate);
    }
  }

  if (filterValues.type) params.append('fapiaoType', filterValues.type);
  if (filterValues.submittedBy) params.append('submittedBy', filterValues.submittedBy);
  if (filterValues.invoiceComment) params.append('invoiceComment', filterValues.invoiceComment);

  return params.toString();
};

// Smart tooltip component that only shows when text is truncated
const TruncatedCell = React.memo(({ text, className = 'cell-text', estimatedCharWidth = 8, cellWidth = 100 }) => {
  const cellRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const content = text || '--';

  // Check if text might be truncated based on character count and estimated width
  const checkTruncation = () => {
    const textLength = content.length;
    // Estimate if text will be truncated (approximate calculation)
    // This is a simple estimation - actual truncation depends on font, characters, etc.
    const estimatedTextWidth = textLength * estimatedCharWidth;
    return estimatedTextWidth > cellWidth - 10; // Allow for some padding
  };

  useEffect(() => {
    // Set truncated state based on estimation
    setIsTruncated(checkTruncation());
  }, [text, cellWidth]);

  return isTruncated ? (
    <Tooltip title={content} placement="topLeft">
      <div className={className} ref={cellRef}>{content}</div>
    </Tooltip>
  ) : (
    <div className={className}>{content}</div>
  );
});

const InvoiceTable = forwardRef(({ onDataChange, filterValues: externalFilterValues = {}, onFilterChange, onSelectionChange }, ref) => {
  const { theme, config } = useAppConfig();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showPopover, setShowPopover] = useState(false);
  const [errorInvoiceId, setErrorInvoiceId] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [showFilterRow, setShowFilterRow] = useState(true);
  const didMountRef = useRef(false);
  const { t } = useTranslation();
  const tableRef = useRef(null);
  const [computedColumnWidths, setComputedColumnWidths] = useState({});
  const firstColWidthRef = useRef(null);
  const firstColObserverRef = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const scrollRafRef = useRef(null);
  const scrollListenerRef = useRef(null);
  const isTableVisibleRef = useRef(true);
  const intersectionObserverRef = useRef(null);
  const customHeaderRef = useRef(null);
  const filterRowRef = useRef(null);
  const lastScrollLeftRef = useRef(0);
  const lastScrollTimeRef = useRef(0);
  const customHeaderContainerRef = useRef(null);
  const filterRowContainerRef = useRef(null);
  const fixedHeaderRef = useRef(false);
  const headerRectTopRef = useRef(0);
  const tableRectWidthRef = useRef(0);

  // 使用外部传入的filterValues
  const filterValues = externalFilterValues;
  console.log(filterValues, "filterValues")

  // 计算表格列的实际宽度
  const calculateColumnWidths = useCallback(() => {
    if (!document.querySelector('.ant-table')) {
      console.log('Table not found in DOM');
      return;
    }

    // 使用 requestAnimationFrame 优化性能
    requestAnimationFrame(() => {
      try {
        // 查找 Antd Table
        const tableContainer = document.querySelector('.ant-table');
        if (!tableContainer) {
          console.log('Table container not found');
          return;
        }

        console.log('Found table container:', tableContainer);

        // 查找 colgroup（更准确的列宽信息）
        const colgroup = tableContainer.querySelector('colgroup');
        if (!colgroup) {
          console.log('Colgroup not found, trying thead approach');
          // 如果没有colgroup，回退到thead方法
          return calculateFromThead(tableContainer);
        }

        console.log('Found colgroup:', colgroup);

        const cols = colgroup.querySelectorAll('col');
        const widths = {};

        console.log(`Found ${cols.length} columns in colgroup`);

        // 使用 getBoundingClientRect 获取实际渲染宽度
        cols.forEach((col, index) => {
          const rect = col.getBoundingClientRect();
          let width = rect.width;

          // 如果 col 元素没有宽度，尝试从style或computedStyle获取
          if (width === 0) {
            const computedStyle = window.getComputedStyle(col);
            width = parseFloat(computedStyle.width) || 0;
          }

          console.log(`Column ${index}: ${width}px (from getBoundingClientRect)`);

          if (width > 0) {
            widths[index] = width;
          }
        });

        // 如果colgroup方法没有获取到宽度，尝试thead方法
        if (Object.keys(widths).length === 0) {
          console.log('Colgroup method failed, trying thead method');
          return calculateFromThead(tableContainer);
        }

        console.log('Computed column widths:', widths);

        // 使用批量更新避免多次渲染
        setComputedColumnWidths(prevWidths => {
          // 只在宽度真正改变时才更新
          const hasChanged = JSON.stringify(prevWidths) !== JSON.stringify(widths);
          if (hasChanged) {
            // 启动第一个col的监控和滚动监听
            requestAnimationFrame(() => {
              startFirstColMonitoring();
              setupScrollListener();
              setupVisibilityObserver();
            });
          }
          return hasChanged ? widths : prevWidths;
        });

      } catch (error) {
        console.error('Error calculating column widths:', error);
      }
    });
  }, []);

  // 备用方法：从表头获取宽度
  const calculateFromThead = useCallback((tableContainer) => {
    try {
      const thead = tableContainer.querySelector('thead');
      if (!thead) {
        console.log('Thead not found');
        return;
      }

      const headerRow = thead.querySelector('tr');
      if (!headerRow) {
        console.log('Header row not found');
        return;
      }

      const ths = headerRow.querySelectorAll('th');
      const widths = {};

      console.log(`Found ${ths.length} columns in thead`);

      ths.forEach((th, index) => {
        // 使用 getBoundingClientRect 获取实际渲染宽度
        const rect = th.getBoundingClientRect();
        const width = rect.width;

        console.log(`Column ${index}: ${width}px (from th getBoundingClientRect)`);

        if (width > 0) {
          widths[index] = width;
        }
      });

      console.log('Computed column widths from thead:', widths);

      // 使用批量更新避免多次渲染
      setComputedColumnWidths(prevWidths => {
        const hasChanged = JSON.stringify(prevWidths) !== JSON.stringify(widths);
        if (hasChanged) {
          // 启动第一个col的监控和滚动监听
          requestAnimationFrame(() => {
            startFirstColMonitoring();
            setupScrollListener();
            setupVisibilityObserver();
          });
        }
        return hasChanged ? widths : prevWidths;
      });

    } catch (error) {
      console.error('Error in calculateFromThead:', error);
    }
  }, []);

  // 防抖优化的列宽计算
  const debouncedCalculateColumnWidths = useMemo(() => {
    let timeoutId;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        calculateColumnWidths();
      }, 100); // 100ms防抖延迟
    };
  }, [calculateColumnWidths]);

  // Create fetchData function outside useEffect
  const fetchData = async () => {
    setLoading(true);
    try {
      // 构建查询参数
      const queryParams = buildApiQueryParams({
        ...filterValues,
        page: currentPage,
        limit: pageSize,

      });

      // 使用 axiosInstance 替代 axios
      const url = `/invoice${queryParams ? `?${queryParams}` : ''}`;
      console.log('Fetching data with URL:', url);

      const response = await axiosInstance.get(url);

      if (response.status !== 200) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = response.data;
      console.log('API Response:', data);

      // 转换数据
      const transformedData = transformInvoiceData(data.items);
      setFilteredData(transformedData);
      setTotalItems(data.total);

      // 调用数据变化回调
      if (onDataChange) {
        onDataChange({ items: transformedData, total: data.total, totals: data.totals });
      }

      // 使用防抖的列宽计算
      debouncedCalculateColumnWidths();
    } catch (error) {
      console.error('Error fetching invoice data:', error);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // 获取数据的 useEffect
  useEffect(() => {
    console.log('filterValues', filterValues);
    fetchData();
  }, [filterValues, currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // 监听数据变化，重新计算列宽（优化版本）
  useEffect(() => {
    if (filteredData.length > 0) {
      // 使用防抖的计算函数
      debouncedCalculateColumnWidths();
    }
  }, [filteredData, debouncedCalculateColumnWidths]);

  // 监听窗口大小变化，重新计算列宽
  useEffect(() => {
    const handleResize = () => {
      debouncedCalculateColumnWidths();
      const tableContainer = document.querySelector('.ant-table');
      if (tableContainer) {
        const tableRect = tableContainer.getBoundingClientRect();
        tableRectWidthRef.current = tableRect.width;
        console.log(tableRectWidthRef.current, "tableRectWidthRef.current", customHeaderContainerRef.current, filterRowContainerRef.current)

        Object.assign(customHeaderContainerRef.current.style, {

          width: `${tableRectWidthRef.current}px`,
        });

        Object.assign(filterRowContainerRef.current.style, {
          width: `${tableRectWidthRef.current}px`,
        });

      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [debouncedCalculateColumnWidths]);

  // 组件卸载时清理第一个col的监控器
  useEffect(() => {
    // 监听字体加载完成事件
    const handleFontsReady = () => {
      console.log('Fonts loaded, recalculating column widths');
      debouncedCalculateColumnWidths();
    };

    // 如果document.fonts API可用
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(handleFontsReady);
    }

    // 备用方案：延迟执行
    const fontLoadTimeout = setTimeout(() => {
      handleFontsReady();
    }, 3000);

    return () => {
      if (firstColObserverRef.current) {
        firstColObserverRef.current.disconnect();
      }
      cleanupScrollListener();
      clearTimeout(fontLoadTimeout);
    };
  }, [debouncedCalculateColumnWidths]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    fetchData,
    clearSelection: () => setSelectedRowKeys([])
  }));

  // 处理过滤器变化
  const handleFilterChange = (newFilterValues) => {
    console.log('newFilterValues', newFilterValues);
    // 重置到第一页
    setCurrentPage(1);

    // 通知外部组件过滤器变化
    if (onFilterChange) {
      onFilterChange(newFilterValues);
    }
  };

  // 清除所有过滤器
  const handleClearFilters = () => {
    setCurrentPage(1);

    // 通知外部组件过滤器被清除
    if (onFilterChange) {
      onFilterChange({});
    }
  };

  // Handler for pagination change
  const handlePaginationChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  // Handler for filter icon click to toggle filter row
  const toggleFilterRow = () => {
    setShowFilterRow(!showFilterRow);
  };

  // Handler for row selection
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys, selectedRows) => {
      setSelectedRowKeys(selectedKeys);

      // Call the onSelectionChange callback if provided
      if (onSelectionChange) {
        // Get the full data for selected invoices
        const selectedInvoices = filteredData.filter(invoice =>
          selectedKeys.includes(invoice.id)
        );
        console.log('selectedInvoices', selectedInvoices);
        console.log('selectedKeys', selectedKeys);
        onSelectionChange(selectedKeys, selectedInvoices);
      }
    },
  };

  // Define expandable row content
  const expandedRowRender = (record) => {
    console.log('record', record);
    if (!record.invoiceDetails || record.invoiceDetails.length === 0) {
      return (
        <div style={{ padding: '20px', backgroundColor: '#fafafa' }}>
          <p><strong>{t('invoice.detailsInfo')}：</strong> {record.id} - {record.customerName}</p>
          <p><strong>{t('invoice.notes')}：</strong> {record.comment}</p>
        </div>
      );
    }

    const lineItemColumns = [
      {
        title: t('invoiceDetails.lineNo'),
        dataIndex: 'lineNo',
        key: 'lineNo',
        width: 80,
        align: 'center',
      },
      {
        title: t('invoiceDetails.description'),
        dataIndex: 'lineDescription',
        key: 'lineDescription',
        width: 250,
      },
      {
        title: t('invoiceDetails.qty'),
        dataIndex: 'sellingShipQty',
        key: 'sellingShipQty',
        width: 80,
        align: 'right',
        render: (text, record) => `${parseFloat(record.sellingShipQty).toFixed(2)} ${record.salesUm}`,
      },
      {
        title: t('invoiceDetails.unitPrice'),
        dataIndex: 'docUnitPrice',
        key: 'docUnitPrice',
        width: 120,
        align: 'right',
        render: (text) => `¥${parseFloat(text).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      },
      {
        title: t('invoiceDetails.subtotal'),
        dataIndex: 'docExtPrice',
        key: 'docExtPrice',
        width: 120,
        align: 'right',
        render: (text, record) => {
          const amount = record.dspExtPrice !== undefined && record.dspExtPrice !== null
            ? parseFloat(record.dspExtPrice)
            : parseFloat(text);
          return `¥${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
      },
      {
        title: t('invoiceDetails.taxRate'),
        dataIndex: 'taxPercent',
        key: 'taxPercent',
        width: 100,
        align: 'right',
        render: (text) => `${parseFloat(text).toFixed(2)}%`,
      },
      {
        title: t('invoiceDetails.taxTotal'),
        dataIndex: 'taxTotal',
        key: 'taxTotal',
        width: 100,
        align: 'right',
        render: (text, record) => {
          // Use backend calculated tax amount if available, otherwise calculate
          const taxAmount = record.dspLineTax !== undefined && record.dspLineTax !== null
            ? parseFloat(record.dspLineTax)
            : parseFloat(record.docExtPrice) * parseFloat(record.taxPercent) / 100;
          return `¥${taxAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
        className: 'highlighted-column',
      },
      {
        title: t('invoiceDetails.lineTotal'),
        dataIndex: 'lineTotal',
        key: 'lineTotal',
        width: 120,
        align: 'right',
        render: (text, record) => {
          // Use backend calculated line total if available, otherwise calculate
          const lineTotal = record.dspLineTotal !== undefined && record.dspLineTotal !== null
            ? parseFloat(record.dspLineTotal)
            : (() => {
              const subtotal = record.dspExtPrice !== undefined && record.dspExtPrice !== null
                ? parseFloat(record.dspExtPrice)
                : parseFloat(record.docExtPrice || 0);
              const taxAmount = record.dspLineTax !== undefined && record.dspLineTax !== null
                ? parseFloat(record.dspLineTax)
                : parseFloat(record.docExtPrice || 0) * parseFloat(record.taxPercent || 0) / 100;
              return subtotal + taxAmount;
            })();
          return `¥${lineTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
        className: 'highlighted-column',
      },
    ];

    // Calculate totals from invoice details
    const subtotal = record.invoiceDetails.reduce((sum, item) =>
      sum + parseFloat(item.dspExtPrice || item.docExtPrice || 0), 0);
    const taxTotal = record.invoiceDetails.reduce((sum, item) => {
      // Use backend calculated tax amount if available, otherwise calculate
      const taxAmount = item.dspLineTax !== undefined && item.dspLineTax !== null
        ? parseFloat(item.dspLineTax || 0)
        : parseFloat(item.docExtPrice || 0) * parseFloat(item.taxPercent || 0) / 100;
      return sum + taxAmount;
    }, 0);
    const invoiceTotal = subtotal + taxTotal;
    console.log(record.invoiceDetails, "DDDDD")
    return (
      <div style={{ padding: '10px', backgroundColor: '#fafafa' }}>
        <div style={{
          fontSize: '14px',
          color: '#44464f',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          lineHeight: '20px',
          letterSpacing: '0.1px',
          fontWeight: '500',
          padding: '10px',
          marginBottom: '10px',
        }}>
          {t('invoice.details')}
        </div>
        <Table
          columns={lineItemColumns}
          dataSource={record.invoiceDetails.map((item, index) => ({ ...item, lineNo: index + 1 }))}
          pagination={false}
          bordered
          size="small"
          rowKey="lineNo"
          style={{
            borderRadius: '4px',
            fontSize: '12px',
          }}
          className="detail-table"
        />
      </div>
    );
  };

  // Handler for error cell click
  const handleErrorClick = (invoiceId) => {
    setErrorInvoiceId(invoiceId);
    setShowPopover(true);
  };

  // Define table columns
  const columns = [
    {
      title: t('invoice.postDate'),
      dataIndex: 'postDate',
      key: 'postDate',
      className: 'cell cell-date',
      width: 100,
      render: (text) => <TruncatedCell text={text} />,
    },
    {
      title: t('invoice.erpInvoiceId'),
      dataIndex: 'id',
      key: 'id',
      className: 'cell cell-id',
      width: 150,
      render: (text) => {
        console.log(config, "config")
        const url = generateErpUrl(text, config);
        return <a href={url} target="_blank" rel="noopener noreferrer"><TruncatedCell text={text} className="cell-text cell-link" /></a>;
      },
    },
    {
      title: t('invoice.fapiaoType'),
      dataIndex: 'type',
      key: 'type',
      className: 'cell cell-type',
      width: 120,
      render: (text) => {
        if (text === "0") {
          return <div className="cell-text">{t('invoice.specialVat')}</div>;
        } else if (text === "1") {
          return <div className="cell-text">{t('invoice.generalVat')}</div>;
        } else {
          return <TruncatedCell text={text} />;
        }
      },
    },
    {
      title: t('invoice.customerName'),
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'cell cell-customer',
      width: 180,
      render: (text) => <TruncatedCell text={text} />,
    },
    {
      title: t('invoice.invoiceAmount'),
      dataIndex: 'invoiceAmount',
      key: 'invoiceAmount',
      className: 'cell cell-amount',
      width: 150,
      render: (text, record) => {
        console.log(text, "invoiceAmount")
        const formattedAmount = text !== null && text !== undefined ?
          `¥${parseFloat(text).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '--';

        return (
          <div className='cell-text'>
            {formattedAmount}
          </div>
        );
      },
    },
    {
      title: t('invoice.comment'),
      dataIndex: 'invoiceComment',
      key: 'invoiceComment',
      className: 'cell cell-comment',
      width: 100,
      render: (text) => <TruncatedCell text={text} />,
    },
    {
      title: t('invoice.status'),
      dataIndex: 'status',
      key: 'status',
      className: 'cell cell-status',
      width: 100,
      render: (text, record) => {
        return <div onClick={() => text === 'ERROR' && handleErrorClick(record.id)}>
          {record.comment ?
            <Tooltip title={record.comment} placement="top" >
              <div>
                <StatusChip status={text} />
              </div>
            </Tooltip>
            :
            <StatusChip status={text} />
          }
        </div>
      },
    },
    {
      title: t('invoice.einvoiceId'),
      dataIndex: 'einvoiceId',
      key: 'einvoiceId',
      className: 'cell cell-einvoice-id',
      width: 120,
      render: (text) => text ? <TruncatedCell text={text} /> : <div className="cell-empty">--</div>,
    },
    {
      title: t('invoice.einvoicePdf'),
      dataIndex: 'hasPdf',
      key: 'hasPdf',
      className: 'cell cell-pdf',
      width: 110,
      render: (hasPdf, record) => {
        console.log(record, hasPdf, "record")
        return hasPdf ? <PdfChip record={record} /> : <div className="cell-empty">--</div>
      },
    },
    {
      title: t('invoice.einvoiceDate'),
      dataIndex: 'einvoiceDate',
      key: 'einvoiceDate',
      className: 'cell cell-einvoice-date',
      width: 100,
      render: (text) => <TruncatedCell text={text} />,
    },
    {
      title: t('invoice.submittedBy'),
      dataIndex: 'submittedBy',
      key: 'submittedBy',
      className: 'cell cell-submitted-by',
      width: 100,
      render: (text) => text ? <TruncatedCell text={text} /> : <div className="cell-empty">--</div>,
    },
  ];

  // Define table configurations
  const tableConfig = {
    rowSelection: {
      ...rowSelection,
      selectedRowKeys,
      columnWidth: 32,
      getCheckboxProps: record => ({
        style: {
          backgroundColor: selectedRowKeys.includes(record.id) ? '#FFF3E5' : ''
        }
      }),
    },
    expandable: {
      expandedRowRender,
      expandIcon: ({ expanded, onExpand, record, ...props }) => (
        <div className="icon" onClick={e => onExpand(record, e)}>
          <span style={{ color: '#808080', cursor: 'pointer', fontVariationSettings: "'FILL' 0, 'wght' 200, 'GRAD' 0, 'opsz' 24" }} className="material-symbols-outlined">
            {expanded ? 'indeterminate_check_box' : 'add_box'}
          </span>
        </div>
      ),
      expandedRowKeys,
      onExpand: (expanded, record) => {
        setExpandedRowKeys(expanded
          ? [...expandedRowKeys, record.id]
          : expandedRowKeys.filter(id => id !== record.id)
        );
      }
    },
    rowClassName: (record, index) => {
      const isSelected = selectedRowKeys.includes(record.id);
      const baseClass = index % 2 === 0 ? 'table-body-row' : 'table-body-row-alternate';
      return isSelected ? `${baseClass} selected-row` : baseClass;
    },
    dataSource: filteredData.map(item => ({ ...item, key: item.id })),
    columns: columns,
    pagination: {
      current: currentPage,
      pageSize: pageSize,
      total: totalItems,
      onChange: handlePaginationChange,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `${t('invoice.total')} ${total} ${t('invoice.records')}`
    },
    bordered: false,
    className: 'invoice-table-ant',
    size: 'middle',
    rowKey: 'id',
    tableLayout: 'fixed',
    scroll: { x: "max-content", y: "calc(100vh - 335px)" },
    showHeader: false,
    style: { width: '100%' },
    loading: loading
  };

  // Handler for select all checkbox
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all visible rows
      const currentPageIds = filteredData.map(item => item.id);
      setSelectedRowKeys(currentPageIds);

      // Call the onSelectionChange callback if provided
      if (onSelectionChange) {
        onSelectionChange(currentPageIds, filteredData);
      }
    } else {
      // Deselect all
      setSelectedRowKeys([]);

      // Call the onSelectionChange callback if provided
      if (onSelectionChange) {
        onSelectionChange([], []);
      }
    }
  };

  // Check if all current page items are selected
  const allSelected = filteredData.length > 0 &&
    filteredData.every(item => selectedRowKeys.includes(item.id));

  const handleScroll = (e) => {
    console.log(e, "event")
  }

  // 监控第一个col（图标列）的宽度变化
  const startFirstColMonitoring = useCallback(() => {
    const tableContainer = document.querySelector('.ant-table');
    if (!tableContainer) return;

    const colgroup = tableContainer.querySelector('colgroup');
    if (!colgroup) return;

    const firstCol = colgroup.querySelector('col:first-child');
    if (!firstCol) return;

    // 记录初始宽度
    const rect = firstCol.getBoundingClientRect();
    firstColWidthRef.current = rect.width;

    console.log('Starting first col monitoring, initial width:', rect.width);

    // 清理之前的观察器
    if (firstColObserverRef.current) {
      firstColObserverRef.current.disconnect();
    }

    // 查找自定义表头中的图标元素
    const iconElement = document.querySelector('.custom-table-header .material-symbols-outlined');

    // 使用MutationObserver监控DOM变化
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // 监控style变化或者子节点变化（图标加载）
        if (mutation.type === 'attributes' || mutation.type === 'childList') {
          setTimeout(() => checkFirstColWidth(), 100);
        }
      });
    });

    // 观察第一个col的变化
    observer.observe(firstCol, {
      attributes: true,
      attributeFilter: ['style', 'width'],
      childList: true,
      subtree: true
    });

    // 如果图标元素存在，也监控它
    if (iconElement) {
      observer.observe(iconElement, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    // 同时使用定期检查作为备用方案（频率降低）
    const intervalCheck = setInterval(() => {
      checkFirstColWidth();
    }, 1000); // 每1秒检查一次

    // 存储清理函数
    firstColObserverRef.current = {
      disconnect: () => {
        observer.disconnect();
        clearInterval(intervalCheck);
      }
    };

    // 多次延迟检查，确保图标有时间加载
    [500, 1000, 2000, 3000].forEach(delay => {
      setTimeout(() => {
        checkFirstColWidth();
      }, delay);
    });

  }, []);

  // 检查第一个col宽度是否发生变化
  const checkFirstColWidth = useCallback(() => {
    const tableContainer = document.querySelector('.ant-table');
    if (!tableContainer) return;

    const colgroup = tableContainer.querySelector('colgroup');
    if (!colgroup) return;

    const firstCol = colgroup.querySelector('col:first-child');
    if (!firstCol) return;

    const rect = firstCol.getBoundingClientRect();
    const currentWidth = rect.width;
    const previousWidth = firstColWidthRef.current;

    // 如果宽度发生了显著变化（超过2px）
    if (Math.abs(currentWidth - previousWidth) > 2) {
      console.log(`First col width changed from ${previousWidth}px to ${currentWidth}px`);
      firstColWidthRef.current = currentWidth;

      // 重新计算所有列宽
      debouncedCalculateColumnWidths();
    }
  }, [debouncedCalculateColumnWidths]);

  // 处理表格滚动同步
  const handleTableScroll = useCallback((event) => {
    // 如果表格不可见，跳过同步以节省性能
    if (!isTableVisibleRef.current) {
      return;
    }

    const scrollLeft = event.target.scrollLeft;
    const currentTime = performance.now();

    // 高频率节流：如果距离上次更新不到4ms (约240fps)，且移动距离小于1px，则跳过
    if (currentTime - lastScrollTimeRef.current < 4 &&
      Math.abs(scrollLeft - lastScrollLeftRef.current) < 1) {
      return;
    }

    lastScrollTimeRef.current = currentTime;
    lastScrollLeftRef.current = scrollLeft;

    // 直接同步，不使用requestAnimationFrame以减少延迟
    // 使用translate3d启用硬件加速
    const transform = `translate3d(-${scrollLeft}px, 0, 0)`;

    // 同步自定义表头滚动
    if (customHeaderRef.current) {
      customHeaderRef.current.style.transform = transform;
    }

    // 同步过滤行滚动
    if (filterRowRef.current) {
      filterRowRef.current.style.transform = transform;
    }

    // 只在开发环境显示日志，并且限制频率
    if (process.env.NODE_ENV === 'development' && scrollLeft % 100 === 0) {
      console.log('Table scroll sync:', scrollLeft);
    }
  }, []);

  // 缓存DOM元素引用
  const cacheElementRefs = useCallback(() => {
    // 缓存自定义表头元素的内部行（用于横向 transform）
    const customHeaderRow = document.querySelector('.custom-table-header .table-row');
    if (customHeaderRow) {
      customHeaderRef.current = customHeaderRow;
      // 预设transform属性以优化性能
      customHeaderRow.style.transform = 'translate3d(0, 0, 0)';
    }

    // 缓存自定义表头容器（用于纵向固定）
    const customHeaderContainer = document.querySelector('.custom-table-header');
    if (customHeaderContainer) {
      customHeaderContainerRef.current = customHeaderContainer;
    }

    // 缓存过滤行内部行（用于横向 transform）
    const filterRowInner = document.querySelector('.filter-row .table-row');
    if (filterRowInner) {
      filterRowRef.current = filterRowInner;
      filterRowInner.style.transform = 'translate3d(0, 0, 0)';
    }

    // 缓存过滤行容器（用于纵向固定）
    const filterRowContainer = document.querySelector('.filter-row');
    if (filterRowContainer) {
      filterRowContainerRef.current = filterRowContainer;
    }

    console.log('Cached DOM elements:', {
      customHeaderRow: !!customHeaderRef.current,
      customHeaderContainer: !!customHeaderContainerRef.current,
      filterRowInner: !!filterRowRef.current,
      filterRowContainer: !!filterRowContainerRef.current
    });
  }, []);

  // 设置可见性观察器
  const setupVisibilityObserver = useCallback(() => {
    const tableContainer = document.querySelector('.custom-table-header');
    if (!tableContainer || !('IntersectionObserver' in window)) {
      return;
    }

    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isTableVisibleRef.current = entry.isIntersecting;
          if (process.env.NODE_ENV === 'development') {
            console.log('Table visibility:', entry.isIntersecting);
          }
        });
      },
      {
        rootMargin: '50px', // 提前50px开始观察
        threshold: 0.1 // 10%可见即认为可见
      }
    );

    observer.observe(tableContainer);
    intersectionObserverRef.current = observer;
  }, []);

  // 设置表格滚动监听
  const setupScrollListener = useCallback(() => {
    // 清理之前的监听器
    if (scrollListenerRef.current) {
      scrollListenerRef.current.removeEventListener('scroll', handleTableScroll);
    }

    // 查找实际的滚动容器
    const tableContainer = document.querySelector('.ant-table');
    if (!tableContainer) {
      console.log('Table container not found for scroll setup');
      return;
    }

    // Antd Table的滚动容器通常是 .ant-table-body 或 .ant-table-content
    let scrollContainer = tableContainer.querySelector('.ant-table-body');
    if (!scrollContainer) {
      scrollContainer = tableContainer.querySelector('.ant-table-content');
    }
    if (!scrollContainer) {
      scrollContainer = tableContainer.querySelector('.ant-table-container');
    }
    if (!scrollContainer) {
      // 如果都找不到，使用table容器本身
      scrollContainer = tableContainer;
    }

    console.log('Setting up scroll listener on:', scrollContainer);

    // 先缓存DOM元素引用
    cacheElementRefs();

    // 添加滚动监听
    scrollContainer.addEventListener('scroll', handleTableScroll, { passive: true });
    scrollListenerRef.current = scrollContainer;

    // 初始化滚动位置
    const initialScrollLeft = scrollContainer.scrollLeft;
    if (initialScrollLeft > 0) {
      handleTableScroll({ target: { scrollLeft: initialScrollLeft } });
    }
  }, [handleTableScroll, cacheElementRefs]);

  // 清理滚动监听
  const cleanupScrollListener = useCallback(() => {
    if (scrollListenerRef.current) {
      scrollListenerRef.current.removeEventListener('scroll', handleTableScroll);
      scrollListenerRef.current = null;
    }
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
      intersectionObserverRef.current = null;
    }
    // 清理DOM引用
    customHeaderRef.current = null;
    filterRowRef.current = null;
    customHeaderContainerRef.current = null;
    filterRowContainerRef.current = null;
  }, [handleTableScroll]);

  // -------------------------------
  // 处理纵向滚动以实现 viewport 固定
  // -------------------------------
  const updateVerticalSticky = useCallback(() => {
    // 如果引用不存在，尝试重新查询
    if (!customHeaderContainerRef.current) {
      customHeaderContainerRef.current = document.querySelector('.custom-table-header');
    }
    if (!filterRowContainerRef.current) {
      filterRowContainerRef.current = document.querySelector('.filter-row');
    }

    // 若仍未找到元素则终止
    if (!customHeaderContainerRef.current || !filterRowContainerRef.current) {
      return;
    }

    console.log(customHeaderContainerRef.current, "customHeaderContainerRef.current")
    console.log(filterRowContainerRef.current, "filterRowContainerRef.current")

    // 主滚动容器(document) 已不需要 scrollTop 逻辑
    // 通过 header 自身位置判断是否需要固定
    const headerRect = customHeaderContainerRef.current.getBoundingClientRect();
    console.log(headerRect.top, "headerRect.top")

    // 当 header 顶部已经碰到视口顶端（<=0）则启用 fixed
    if (headerRect.top <= 0 && !fixedHeaderRef.current) {
      fixedHeaderRef.current = true;
      const headerHeight = headerRect.height;

      Object.assign(customHeaderContainerRef.current.style, {
        position: 'fixed',
        top: `0px`,
        left: `${headerRect.left}px`,
        width: `${headerRect.width}px`,
        zIndex: 100,
      });

      Object.assign(filterRowContainerRef.current.style, {
        position: 'fixed',
        top: `${headerHeight}px`,
        left: `${headerRect.left}px`,
        width: `${headerRect.width}px`,
        zIndex: 99,
      });
    }
    const scrollY = window.scrollY || window.pageYOffset;
    console.log(scrollY, "scrollY")
    if (scrollY <= headerRectTopRef.current && fixedHeaderRef.current) {
      fixedHeaderRef.current = false;
      customHeaderContainerRef.current.style.position = 'sticky';
      filterRowContainerRef.current.style.position = 'sticky';
      customHeaderContainerRef.current.style.top = '';
      filterRowContainerRef.current.style.top = '';
      customHeaderContainerRef.current.style.left = '';
      filterRowContainerRef.current.style.left = '';
      filterRowContainerRef.current.style.width = '';
      filterRowContainerRef.current.style.height = '';
      filterRowContainerRef.current.style.zIndex = '';
      customHeaderContainerRef.current.style.width = '';

    }
  }, []);

  // 在 main-content 滚动与窗口尺寸变化时更新
  useEffect(() => {
    // const mainContent = document.querySelector('.main-content');
    // if (!mainContent) return;



    // 首次调用
    updateVerticalSticky({ target: { scrollTop: 0 } });
    const headerRect = customHeaderContainerRef.current.getBoundingClientRect()
    headerRectTopRef.current = headerRect.top
    console.log(headerRectTopRef.current, "headerRectTopRef.current")


    // // 监听 scroll
    // const onScroll = () => {
    //   updateVerticalSticky();
    // };
    window.addEventListener('scroll', () => updateVerticalSticky(), { passive: true });

    // 监听 resize
    window.addEventListener('resize', updateVerticalSticky);

    return () => {
      document.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateVerticalSticky);
    };
  }, [updateVerticalSticky]);

  return (
    <div>
      <style>
        {`
          .invoice-table-ant .selected-row,
          .invoice-table-ant .selected-row:hover {
            background-color: #FFF3E5 !important;
          }
          
          .invoice-table-ant .selected-row td {
            background-color: #FFF3E5 !important;
          }
          
          .invoice-table-ant .ant-table-row-selected > td {
            background-color: #FFF3E5 !important;
          }

          .invoice-table-ant .ant-checkbox-checked .ant-checkbox-inner {
            background-color: ${theme.primary} !important;
            border-color: ${theme.primary} !important;
          }

          .invoice-table-ant .ant-pagination-item-active {
            border-color: ${theme.primary} !important;
          }

          .invoice-table-ant .ant-pagination-item-active a {
            color: ${theme.primary} !important;
          }
           
          .custom-checkbox {
            appearance: none;
            width: 16px;
            height: 16px;
            border: 1px solid #d9d9d9;
            border-radius: 2px;
            position: relative;
            cursor: pointer;
            background-color: #fff;
            transition: all 0.2s;
            outline: none;
          }
           
          .custom-checkbox:checked {
            background-color: ${theme.primary};
            border-color: ${theme.primary};
          }
           
          .custom-checkbox:checked:after {
            content: '';
            position: absolute;
            left: 4px;
            top: 1px;
            width: 6px;
            height: 9px;
            border: solid white;
            border-width: 0 2px 2px 0;
            transform: rotate(45deg);
            display: block;
          }
          
          .invoice-table-ant .calculated-amount {
            color: ${theme.primary} !important;
            font-weight: 500;
          }
          
          .detail-table .ant-table-thead > tr > th,
          .detail-table .ant-table-tbody > tr > td {
            font-size: 12px;
            padding: 6px 8px;
          }
          
          .detail-table .ant-table {
            font-size: 12px;
          }
          
          .expand-icon {
            color: #bbb;
            font-size: 22px;
            background-color: transparent;
          }
          
          .expand-icon:hover {
            color: #999;
          }
          
          /* 修复滚动时的空余问题 */
          .custom-table-header .table-row,
          .filter-row .table-row {
            min-width: max-content; /* 确保容器足够宽 */
            width: max-content;     /* 让容器宽度等于内容宽度 */
            transform-origin: left center;
            backface-visibility: hidden;
            will-change: transform;
            contain: layout style paint;
          }
          
          /* 实现sticky吸顶功能 */
          .custom-table-header {
            position: sticky;
            top: -24px;               /* 吸顶到滚动容器顶部 */
            z-index: 100;         /* 高优先级 */
            overflow: hidden;     /* 隐藏超出部分 */
            width: 100%;         /* 容器宽度100% */
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          
          .filter-row {
            position: sticky;
            top: 40px;           /* 显示在header下方 */
            z-index: 99;         /* 稍低优先级 */
            overflow: hidden;    /* 隐藏超出部分 */
            width: 100%;        /* 容器宽度100% */
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
        `}
      </style>
      <div className="custom-table-header" style={{ backgroundColor: theme.secondaryContainer }}>
        <div className="table-row" style={{
          transition: 'none', // 禁用过渡动画以提高滚动性能
          willChange: 'transform' // 优化GPU加速
        }}>
          <div className="cell-expand" style={{
            display: 'flex',
            flex: computedColumnWidths[0] ? `0 0 ${computedColumnWidths[0]}px` : "0 0 48px",
            minWidth: 0,
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <span
              className="material-symbols-outlined"
              style={{ cursor: 'pointer', fontVariationSettings: "'FILL' 0, 'wght' 200, 'GRAD' 0, 'opsz' 24", color: '#808080' }}
              onClick={toggleFilterRow}
              title={t('filter.toggleFilter')}
            >
              {'filter_alt'}
            </span>
          </div>
          <div className="cell-checkbox" style={{
            display: 'flex',
            flex: computedColumnWidths[1] ? `0 0 ${computedColumnWidths[1]}px` : "0 0 32px",
            minWidth: 0,
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <input
              type="checkbox"
              title="Select all rows"
              checked={allSelected}
              onChange={handleSelectAll}
              className="custom-checkbox"
              style={{
                width: '16px',
                height: '16px',
                backgroundColor: allSelected ? theme.primary : '#fff',
                borderColor: allSelected ? theme.primary : '#d9d9d9'
              }}
            />
          </div>
          {columns.map((column, index) => {
            // 计算列的索引（加上展开列和选择列）
            const columnIndex = index + 2;
            const computedWidth = computedColumnWidths[columnIndex];

            return (
              <div key={`header-${column.key}`} className={"header-cell cell-" + column.key} style={{
                flex: computedWidth ? `0 0 ${computedWidth}px` : `0 0 ${column.width}px`,
                minWidth: 0
              }}>
                <div className="cell-header-text" title={column.title}>{column.title}</div>
              </div>
            );
          })}
        </div>
      </div>


      <FilterRow
        showFilterRow={showFilterRow}
        columns={columns}
        filterValues={filterValues}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        computedColumnWidths={computedColumnWidths}
      />


      <ConfigProvider theme={{ token: { colorPrimary: theme.primary } }}>
        <Table ref={tableRef} {...tableConfig} />
      </ConfigProvider>
    </div>
  );
});

export default InvoiceTable;